<template>
  <div class="users-view">

    <!-- Composant liste des utilisateurs -->
    <UsersListView 
      :users="users" 
      :loading="loading"
      :pagination="pagination"
      @load="loadUsers"
      @add="showAddUserModal"
      @edit="editUser"
      @delete="confirmDeleteUser"
      @toggle-status="confirmToggleStatus"
      @page-change="handlePageChange"
    />

    <!-- Pagination avec le nouveau composant -->
    <PaginationComponent 
      v-if="!loading && users.length > 0" 
      :current-page="currentPage" 
      :per-page="perPage" 
      :to="to"
      :total="totalUsers" 
      @page-change="handlePageChange"
    />

    <!-- Modals seront implémentés selon les besoins -->
  </div>
</template>

<script>
import { defineComponent, ref, reactive, computed, onMounted } from 'vue'
import UsersListView from './UsersListView.vue'
import PaginationComponent from '@/components/common/PaginationComponent.vue'
import usersService from '@/services/usersService'

export default defineComponent({
  name: 'UsersView',
  components: {
    UsersListView,
    PaginationComponent
  },
  
  setup() {
    // Données d'état
    const users = ref([]);
    const loading = ref(true);
    const search = ref('');
    const filters = reactive({
      role: '',
      active: null
    });
    
    // Gestion des modals
    const currentUser = ref({});
    const showAddModal = ref(false);
    const showEditModal = ref(false);
    const showDeleteModal = ref(false);
    const showStatusModal = ref(false);
    
    // Pagination
    const currentPage = ref(1);
    const perPage = ref(10);
    const totalUsers = ref(0);
    const lastPage = ref(1);
    const to = ref(1);
    // Calcul des informations de pagination pour le composant UsersList
    const pagination = computed(() => ({
      currentPage: currentPage.value,
      perPage: perPage.value,
      total: totalUsers.value,
      lastPage: lastPage.value,
      to: to.value
    }));

    // Méthodes
    const loadUsers = async (filterParams = {}) => {
      try {
        loading.value = true;

        // Préparer les paramètres
        const params = {
          page: currentPage.value,
          per_page: perPage.value,
          sort_by: 'created_at',
          sort_direction: 'desc'
        };

        // Mettre à jour les filtres si fournis
        if (filterParams.search !== undefined) {
          search.value = filterParams.search;
        }
        if (filterParams.role !== undefined) {
          filters.role = filterParams.role;
        }
        if (filterParams.active !== undefined) {
          filters.active = filterParams.active;
        }

        // Ajouter les filtres aux paramètres
        if (search.value) {
          params.search = search.value;
        }
        if (filters.role) {
          params.role = filters.role;
        }
        if (filters.active !== null) {
          params.active = filters.active;
        }

        // Appel à l'API
        const response = await usersService.getUsers(params);
        
        // Mise à jour des données
        if (response.data && response.data.data) {
          users.value = response.data.data;
          
          // Mise à jour de la pagination
          currentPage.value = response.data.current_page;
          lastPage.value = response.data.last_page;
          totalUsers.value = response.data.total;
          perPage.value = response.data.per_page;
          to.value = response.data.to;
        } else {
          users.value = [];
          totalUsers.value = 0;
        }
      } catch (error) {
        console.error('Erreur lors du chargement des utilisateurs:', error);
      } finally {
        loading.value = false;
      }
    };

    const handlePageChange = (page) => {
      currentPage.value = page;
      loadUsers();
    };

    const showAddUserModal = () => {
      currentUser.value = {};
      showAddModal.value = true;
    };

    const editUser = (user) => {
      currentUser.value = { ...user };
      showEditModal.value = true;
    };

    const confirmDeleteUser = (user) => {
      currentUser.value = { ...user };
      showDeleteModal.value = true;
    };

    const confirmToggleStatus = (user) => {
      currentUser.value = { ...user };
      showStatusModal.value = true;
    };

    const saveUser = async (userData) => {
      try {
        loading.value = true;
        
        if (userData.id) {
          // Mise à jour d'un utilisateur existant
          await usersService.updateUser(userData.id, userData);
        } else {
          // Création d'un nouvel utilisateur
          await usersService.createUser(userData);
        }
        
        // Recharger la liste des utilisateurs
        await loadUsers();
        
        // Fermer les modals
        showAddModal.value = false;
        showEditModal.value = false;
      } catch (error) {
        console.error('Erreur lors de la sauvegarde de l\'utilisateur:', error);
      } finally {
        loading.value = false;
      }
    };

    const deleteUser = async () => {
      try {
        loading.value = true;
        
        // Supprimer l'utilisateur
        await usersService.deleteUser(currentUser.value.id);
        
        // Recharger la liste des utilisateurs
        await loadUsers();
        
        // Fermer le modal
        showDeleteModal.value = false;
      } catch (error) {
        console.error('Erreur lors de la suppression de l\'utilisateur:', error);
      } finally {
        loading.value = false;
      }
    };

    const toggleUserStatus = async () => {
      try {
        loading.value = true;
        
        // Changer le statut de l'utilisateur
        await usersService.toggleUserStatus(currentUser.value.id, !currentUser.value.active);
        
        // Recharger la liste des utilisateurs
        await loadUsers();
        
        // Fermer le modal
        showStatusModal.value = false;
      } catch (error) {
        console.error('Erreur lors du changement de statut de l\'utilisateur:', error);
      } finally {
        loading.value = false;
      }
    };

    // Charger les données au montage du composant
    onMounted(() => {
      loadUsers();
    });

    return {
      users,
      loading,
      search,
      filters,
      pagination,
      currentPage,
      perPage,
      to,
      totalUsers,
      lastPage,
      currentUser,
      showAddModal,
      showEditModal,
      showDeleteModal,
      showStatusModal,
      loadUsers,
      handlePageChange,
      showAddUserModal,
      editUser,
      confirmDeleteUser,
      confirmToggleStatus,
      saveUser,
      deleteUser,
      toggleUserStatus
    };
  }
});
</script>

<style scoped>
.users-view {
  padding: 0 0 40px 0;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.page-heading {
  font-size: 24px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
}

.add-user-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  background-color: #0A5C6B;
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 500;
  font-size: 15px;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.add-user-btn:hover {
  background-color: #094854;
}

/* Filtres */
.filters-container {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 24px;
  background-color: white;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);
}

.search-bar {
  flex: 1;
  min-width: 250px;
}

.input-with-icon {
  position: relative;
  width: 100%;
}

.input-with-icon svg {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #9ca3af;
}

.input-with-icon input {
  width: 100%;
  padding: 12px 12px 12px 40px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  background-color: #f9fafb;
  transition: all 0.2s ease;
}

.input-with-icon input:focus {
  outline: none;
  border-color: #0A5C6B;
  background-color: white;
  box-shadow: 0 0 0 3px rgba(10, 92, 107, 0.1);
}

.filter-selects {
  display: flex;
  gap: 12px;
}

.filter-select select {
  padding: 10px 32px 10px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background-color: #f9fafb;
  font-size: 14px;
  min-width: 180px;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%23666666' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E");
  background-position: right 12px center;
  background-repeat: no-repeat;
  cursor: pointer;
}

.filter-select select:focus {
  outline: none;
  border-color: #0A5C6B;
  background-color: white;
  box-shadow: 0 0 0 3px rgba(10, 92, 107, 0.1);
}

@media (max-width: 768px) {
  .page-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
  
  .add-user-btn {
    width: 100%;
    justify-content: center;
  }
  
  .filters-container {
    flex-direction: column;
  }
  
  .filter-selects {
    width: 100%;
    flex-direction: column;
  }
  
  .filter-select {
    width: 100%;
  }
  
  .filter-select select {
    width: 100%;
  }
}
</style>